/**
 * Workshop showcase page — renders all frame shape types with mock data.
 *
 * Displays each frame type in multiple states (idle, streaming, done, error,
 * empty) so all visual variants can be reviewed together.
 * Resizable previews use their frame drag handles where available.
 */

import { ConversationFrame } from "@/workshop/components/shapes/conversation-frame/conversation-frame";
import { EmailFrame } from "@/workshop/components/shapes/email-frame/email-frame";
import { NewsletterFrame } from "@/workshop/components/shapes/newsletter-frame/newsletter-frame";
import { LinkedinFrame } from "@/workshop/components/shapes/linkedin-frame/linkedin-frame";
import { useEffect } from "react";
import {
  ResizableText,
  ResizableCode,
  ResizableSearch,
  ResizableTool,
  ResizableWorkflow,
  ResizableImage,
  ResizableMemory,
  ResizableData,
  ResizableHtml,
  ResizableConnection,
  ResizableConnectApp,
} from "./shapes-page-frames";
import {
  MOCK_TEXT,
  MOCK_CODE,
  MOCK_SEARCH_RESULTS,
  MOCK_TOOL_INPUT,
  MOCK_TOOL_OUTPUT,
  MOCK_IMAGES,
  MOCK_SINGLE_IMAGE,
  MOCK_HTML_CHART,
} from "./shapes-page-data";
import {
  MOCK_PARALLEL,
  MOCK_SEQUENTIAL,
  MOCK_FAILED,
} from "@/workshop/data/mock-workflows";

// Section helper
function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-10" data-testid={`shapes-section-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <h2 className="mb-4 text-sm font-semibold text-white/80">{title}</h2>
      <div className="flex flex-wrap gap-6">
        {children}
      </div>
    </div>
  );
}

function PreviewNode({
  children,
  width = 280,
}: {
  children: React.ReactNode;
  width?: number;
}) {
  return (
    <div className="react-flow__node" style={{ position: "relative", width }}>
      {children}
    </div>
  );
}

// Page
export function ShapesPage() {
  useEffect(() => {
    const root = document.documentElement;
    const previous = {
      desktopDenk: root.dataset.desktopDenk,
      desktopGlass: root.dataset.desktopGlass,
      desktopWorkspace: root.dataset.desktopWorkspace,
      desktopNativeGlass: root.dataset.desktopNativeGlass,
    };

    root.dataset.desktopDenk = "true";
    root.dataset.desktopGlass = "true";
    root.dataset.desktopNativeGlass = "true";
    delete root.dataset.desktopWorkspace;

    return () => {
      if (previous.desktopDenk === undefined) delete root.dataset.desktopDenk;
      else root.dataset.desktopDenk = previous.desktopDenk;
      if (previous.desktopGlass === undefined) delete root.dataset.desktopGlass;
      else root.dataset.desktopGlass = previous.desktopGlass;
      if (previous.desktopWorkspace === undefined) {
        delete root.dataset.desktopWorkspace;
      } else {
        root.dataset.desktopWorkspace = previous.desktopWorkspace;
      }
      if (previous.desktopNativeGlass === undefined) {
        delete root.dataset.desktopNativeGlass;
      } else {
        root.dataset.desktopNativeGlass = previous.desktopNativeGlass;
      }
    };
  }, []);

  return (
    <div className="p-8" data-testid="shapes-page">
      <div className="mb-8">
        <h1 className="text-lg font-bold text-white">
          Frame Shapes
        </h1>
        <p className="mt-1 text-sm text-white/40">
          All frame shape components with mock data in various states. Resizable previews expose drag handles at their bottom-right corners.
        </p>
      </div>

      {/* ---- Text Frames ---- */}
      <Section title="Text Frame">
        <ResizableText
          title="Deployment Summary"
          text={MOCK_TEXT}
          status="done"
          agentName="claude-1"
        />
        <ResizableText
          title="Streaming Response"
          text="Analyzing the codebase structure to identify potential"
          status="streaming"
          agentName="claude-2"
        />
        <ResizableText
          title="Empty Text"
          text=""
          status="idle"
        />
      </Section>

      {/* ---- Code Frames ---- */}
      <Section title="Code Frame">
        <ResizableCode
          title="useDebounce.ts"
          code={MOCK_CODE}
          language="typescript"
          status="done"
          agentName="claude-1"
        />
        <ResizableCode
          title="Generating code..."
          code={"function fibonacci(n: number): number {\n  if (n <= 1) return n;"}
          language="typescript"
          status="streaming"
        />
        <ResizableCode
          title="Empty Snippet"
          code=""
          language="plain"
          status="idle"
          initialWidth={280}
        />
      </Section>

      {/* ---- Search Frames ---- */}
      <Section title="Search Frame">
        <ResizableSearch
          query="react flow custom nodes tutorial"
          results={MOCK_SEARCH_RESULTS}
          status="done"
          agentName="web-agent"
        />
        <ResizableSearch
          query="zustand middleware patterns"
          results={[]}
          status="streaming"
          initialWidth={280}
        />
        <ResizableSearch
          query=""
          results={[]}
          status="idle"
          initialWidth={280}
        />
      </Section>

      {/* ---- Tool Frames ---- */}
      <Section title="Tool Frame">
        <ResizableTool
          title="Database Query"
          toolName="sql_query"
          input={MOCK_TOOL_INPUT}
          output={MOCK_TOOL_OUTPUT}
          status="done"
          agentName="db-agent"
        />
        <ResizableTool
          title="Running Tool"
          toolName="file_search"
          input={'{\n  "pattern": "*.tsx",\n  "directory": "/src"\n}'}
          output=""
          status="streaming"
          initialWidth={280}
        />
        <ResizableTool
          title="Tool Error"
          toolName="http_request"
          input={'{\n  "url": "https://api.example.com/data"\n}'}
          output=""
          status="error"
          initialWidth={280}
        />
      </Section>

      {/* ---- Workflow Frames ---- */}
      <Section title="Workflow Frame">
        <ResizableWorkflow
          title="Weekly Report"
          flowSteps={MOCK_PARALLEL.steps}
          status="streaming"
          agentName="deploy-agent"
          onApprove={() => {}}
          onReject={() => {}}
        />
        <ResizableWorkflow
          title="Data Import"
          flowSteps={MOCK_SEQUENTIAL.steps}
          status="done"
          initialWidth={280}
        />
        <ResizableWorkflow
          title="Failed Build"
          flowSteps={MOCK_FAILED.steps}
          status="error"
          initialWidth={280}
        />
      </Section>

      {/* ---- Image Frames ---- */}
      <Section title="Image Frame">
        <ResizableImage
          title="Design Mockups"
          images={MOCK_IMAGES}
          status="done"
          agentName="design-agent"
        />
        <ResizableImage
          title="Canvas Screenshot"
          images={MOCK_SINGLE_IMAGE}
          status="done"
        />
        <ResizableImage
          title="Loading Images"
          images={[]}
          status="streaming"
          initialWidth={280}
        />
        <ResizableImage
          title="No Images"
          images={[]}
          status="idle"
          initialWidth={280}
        />
      </Section>

      {/* ---- Memory Frames ---- */}
      <Section title="Memory Frame">
        <ResizableMemory
          title="Knowledge Graph"
          agentName="memory-agent"
        />
        <ResizableMemory
          title="Project Context"
          initialWidth={240}
        />
      </Section>

      {/* ---- Conversation Frames ---- */}
      <Section title="Conversation Frame">
        <PreviewNode>
          <ConversationFrame
            title="Research Chat"
            agentName="Assistant"
            isStreaming={true}
          />
        </PreviewNode>
        <PreviewNode>
          <ConversationFrame
            title="Completed Chat"
            agentName="Assistant"
            isStreaming={false}
          />
        </PreviewNode>
      </Section>

      {/* Removed: question-frame shape was deleted in PR3 (concise-agent-replies). */}

      {/* ---- Connection Frames ---- */}
      <Section title="Connection Frame — Hub">
        <ResizableConnection agentName="System" />
        <ResizableConnection agentName="Research Agent" initialWidth={260} />
      </Section>

      <Section title="Connection Frame — Single App">
        <ResizableConnectApp
          appId="gmail"
          reason="I need to send the campaign email you requested."
          agentName="Email Agent"
        />
        <ResizableConnectApp
          appId="slack"
          reason="I'll post the daily summary to your team channel."
          agentName="System"
          initialWidth={220}
        />
        <ResizableConnectApp
          appId="github"
          reason="I need to create a pull request for the changes I made."
          agentName="Dev Agent"
          initialWidth={220}
        />
      </Section>

      {/* ---- Data Frames ---- */}
      <Section title="Data Frame">
        <ResizableData
          title="Revenue by Region"
          agentName="Analyst"
        />
        <ResizableData
          title="User Growth"
          initialWidth={280}
        />
      </Section>

      {/* ---- Email Frames ---- */}
      <Section title="Email Frame">
        <PreviewNode>
          <EmailFrame
            title="Follow-up Email"
            agentName="Writer"
          />
        </PreviewNode>
        <PreviewNode>
          <EmailFrame
            title="Proposal Draft"
            agentName="Writer"
            to="cto@techcorp.io"
            subject="Partnership Proposal — Q2 2026"
            body="Hi Marcus,\n\nFollowing our conversation at the summit, I'd like to formally propose a collaboration between our teams..."
          />
        </PreviewNode>
      </Section>

      {/* ---- Newsletter Frames ---- */}
      <Section title="Newsletter Frame">
        <PreviewNode>
          <NewsletterFrame
            title="Weekly Digest #42"
            agentName="Newsletter"
          />
        </PreviewNode>
      </Section>

      {/* ---- LinkedIn Frames ---- */}
      <Section title="LinkedIn Frame">
        <PreviewNode>
          <LinkedinFrame
            title="AI in 2026 Post"
            agentName="Social"
          />
        </PreviewNode>
        <PreviewNode>
          <LinkedinFrame
            title="Product Launch Post"
            agentName="Social"
            postText="Excited to announce Denker v2.0 is live!\n\nAfter months of building, we've shipped the canvas workspace that lets humans and AI agents co-work visually in real time."
            tags={["#ProductLaunch", "#AI", "#Denker"]}
            charCount="312/3000 characters"
          />
        </PreviewNode>
      </Section>

      {/* ---- HTML Frames ---- */}
      <Section title="HTML Frame">
        <ResizableHtml html={MOCK_HTML_CHART} status="done" agentName="agent-1" />
        <ResizableHtml html="" status="idle" initialWidth={280} />
      </Section>
    </div>
  );
}
